import React, { useEffect, useState } from "react";
import { initializeAOS } from "../Aos";

const sections = [
    { id: "overview", label: "Overview" },
    { id: "data", label: "Data we collect" },
    { id: "usage", label: "How we use data" },
    { id: "sharing", label: "Sharing" },
    { id: "international", label: "International" },
    { id: "rights", label: "Your rights" },
    { id: "retention", label: "Retention" },
    { id: "cookies", label: "Cookies" },
    { id: "security", label: "Security" },
    { id: "children", label: "Children" },
    { id: "changes", label: "Changes" },
    { id: "contact", label: "Contact" },
];

const Card = ({ id, title, children }) => (
    <section
        id={id}
        className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 md:p-8 scroll-mt-32"
        data-aos="fade-up"
    >
        <h3 className="text-lg md:text-xl font-semibold text-[#1E293B] mb-3">
            {title}
        </h3>
        <div className="text-sm md:text-[15px] leading-7 text-[#475569] space-y-3">
            {children}
        </div>
    </section>
);

const PrivacyPolicy = () => {
    const [activeSection, setActiveSection] = useState("overview");

    useEffect(() => {
        initializeAOS();
    }, []);

    useEffect(() => {
        const observers = [];

        sections.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (!el) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setActiveSection(id);
                    }
                },
                { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
            );

            observer.observe(el);
            observers.push(observer);
        });

        return () => observers.forEach((obs) => obs.disconnect());
    }, []);

    return (
        <section className="bg-[#F5F7FB] py-16 md:py-28 px-4">
            <div className="container mx-auto max-w-7xl flex gap-8">

                {/* SIDEBAR */}
                <aside className="hidden lg:block w-[260px]">
                    <div className="sticky top-[32px] bg-white rounded-xl border border-gray-100 p-4">
                        <p className="text-sm font-semibold text-gray-500 mb-3">
                            Privacy policy
                        </p>
                        <nav className="space-y-1">
                            {sections.map((item) => (
                                <a
                                    key={item.id}
                                    href={`#${item.id}`}
                                    className={`block px-3 py-2 text-sm rounded-md transition ${activeSection === item.id
                                            ? "bg-[#F0FDFA] text-[#0D9488] font-medium border-l-2 border-[#0D9488]"
                                            : "text-gray-600 hover:bg-[#F0FDFA] hover:text-[#0D9488]"
                                        }`}
                                >
                                    {item.label}
                                </a>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* CONTENT */}
                <div className="flex-1 space-y-6 max-w-4xl">

                    <div>
                        <h1 className="text-3xl md:text-4xl font-semibold text-[#0F172A]">
                            Privacy Policy
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Effective date: Jan 15, 2025 · Last updated: Jan 15, 2025
                        </p>
                    </div>

                    <div className="bg-[#D1FAF5] text-[#065F46] text-sm rounded-lg px-4 py-3">
                        InvexFlow provides invoice discounting services to businesses.
                        This policy explains what data we collect, why we collect it,
                        and how we handle it.
                    </div>

                    <Card id="overview" title="1. Overview">
                        <p>
                            At <strong>InvexFlow (Pvt.) Ltd.</strong>, we respect your privacy
                            and are committed to protecting it. This policy applies to
                            visitors, clients, and partners using our services.
                        </p>
                    </Card>

                    <Card id="data" title="2. Information we collect">
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Account data: name, email, phone, company</li>
                            <li>Business & KYC data</li>
                            <li>Transaction and invoice data</li>
                            <li>Usage data and cookies</li>
                            <li>Support communications</li>
                        </ul>
                    </Card>

                    <Card id="usage" title="3. How we use information">
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Provide invoice discounting services</li>
                            <li>Verify identity and prevent fraud</li>
                            <li>Process payments and settlements</li>
                            <li>Improve platform performance</li>
                            <li>Meet legal obligations</li>
                        </ul>
                    </Card>

                    <Card id="sharing" title="5. Sharing and disclosures">
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Service providers and processors</li>
                            <li>Banking and financial partners</li>
                            <li>Legal authorities when required</li>
                            <li>Business transfers</li>
                        </ul>
                        <div className="mt-4 bg-[#ECFEFF] text-[#155E75] px-4 py-2 rounded-md text-sm">
                            We do not sell personal information.
                        </div>
                    </Card>

                    <Card id="international" title="6. International data transfers">
                        <p>
                            Your data may be processed outside your country with appropriate
                            safeguards such as standard contractual clauses.
                        </p>
                    </Card>

                    <Card id="retention" title="7. Data retention">
                        <p>
                            We retain information as long as necessary to provide services
                            and comply with legal obligations (typically 2–7 years).
                        </p>
                    </Card>

                    <Card id="rights" title="8. Your rights">
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Access, correct, or delete your data</li>
                            <li>Restrict or object to processing</li>
                            <li>Data portability</li>
                            <li>Withdraw consent</li>
                        </ul>
                    </Card>

                    <Card id="cookies" title="9. Cookies">
                        <p>
                            We use essential and analytics cookies. You may control cookies
                            through browser settings.
                        </p>
                    </Card>

                    <Card id="security" title="10. Security">
                        <p>
                            We implement technical, administrative, and physical safeguards
                            including encryption and access controls.
                        </p>
                    </Card>

                    <Card id="children" title="11. Children">
                        <p>
                            Our services are not directed to children under 16 and we do not
                            knowingly collect their data.
                        </p>
                    </Card>

                    <Card id="changes" title="12. Changes to this policy">
                        <p>
                            We may update this policy periodically. Updates are effective
                            once posted.
                        </p>
                    </Card>

                    <Card id="contact" title="13. Contact us">
                        <ul className="space-y-1">
                            <li>Email: privacy@invexflow.com</li>
                            <li>InvexFlow, 123 Market Street, Suite 400, San Francisco, CA 94105</li>
                            <li>Support: Visit Support to submit a request or view FAQs.</li>
                        </ul>
                        <div className="flex flex-wrap gap-3 mt-5">
                            <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-[#ECFEFF] text-[#0E7490] hover:bg-[#CFFAFE] transition">
                                ⬇ Download PDF
                            </button>
                            <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-[#ECFEFF] text-[#0E7490] hover:bg-[#CFFAFE] transition">
                                ✉ Contact privacy
                            </button>
                        </div>
                    </Card>

                    <div className="bg-white border border-gray-100 rounded-xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-sm">
                        <div className="flex items-start gap-3">
                            <div className="bg-[#ECFEFF] text-[#0D9488] rounded-full p-2">❓</div>
                            <div>
                                <p className="font-medium text-[#0F172A]">Need help?</p>
                                <p className="text-sm text-gray-500">
                                    Questions about your data or DSRs? Our team can assist within 2 business days.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button className="px-4 py-2 text-sm font-medium rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition">
                                View FAQs
                            </button>
                            <button className="px-4 py-2 text-sm font-medium rounded-md bg-[#0D9488] text-white hover:bg-[#0F766E] transition">
                                Submit a data request
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default PrivacyPolicy;